import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useRef } from 'react'
import { api } from '../../api/client'
import { keys } from '../../api/keys'
import { formatCents } from '../../api/parseMoney'
import { mintUuid } from '../../platform/mintUuid'
import { AccountsError, toAccount, type Account } from './useAccounts'

export type ManualAccountInput = {
  name: string
  type: string
  /** Written as `opening_balance` — Trap 3: a manual account's
   *  `current_balance` is DERIVED on read and must never be sent back. */
  openingCents: number
  subtype?: string | null
  mask?: string | null
  institutionName?: string | null
  currency?: string
}

/**
 * One submission attempt's id — `mintUuid`, which survives a missing
 * `crypto.randomUUID` (secure-context-only). Shared with the recurring form's
 * idempotency key; `src/auth/deviceId.ts` keeps its own copy (one browser, not
 * one attempt).
 */
const mintReferenceId = mintUuid

/**
 * `POST /accounts` — create a manual account.
 *
 * **`client_reference_id` is minted once per hook instance and reused across
 * every call.** `ManualAccountForm` keys itself by `'create'` or the row's
 * id (`Accounts.tsx`), so a fresh form mount gets a fresh hook instance and a
 * fresh id, while a resubmit of the SAME open form — the case the idempotency
 * handle exists for — reuses it and cannot create two accounts.
 */
export function useCreateManualAccount(): {
  create: (input: ManualAccountInput) => Promise<Account>
  isPending: boolean
} {
  const client = useQueryClient()
  const referenceId = useRef<string | null>(null)
  referenceId.current ??= mintReferenceId()

  const mutation = useMutation({
    mutationFn: async (input: ManualAccountInput) => {
      const { data, error, response } = await api.POST('/api/v1/accounts', {
        body: {
          name: input.name,
          type: input.type,
          // STRING, not a raw number — trap 2, and `AccountCreate.opening_balance`
          // accepts either on the wire, but every other balance in this module
          // crosses as a decimal string and this keeps the convention singular.
          opening_balance: formatCents(input.openingCents),
          subtype: input.subtype ?? undefined,
          mask: input.mask ?? undefined,
          institution_name: input.institutionName ?? undefined,
          iso_currency_code: input.currency ?? 'CAD',
          client_reference_id: referenceId.current as string,
        },
      })
      if (error !== undefined || data === undefined) throw new AccountsError(error, response.status)
      return toAccount(data)
    },
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.accounts.all() })
    },
  })

  return { create: (input) => mutation.mutateAsync(input), isPending: mutation.isPending }
}

export type ManualAccountEdit = {
  openingCents?: number
  institutionName?: string | null
}

/**
 * `PATCH /accounts/{id}` — edit a manual account.
 *
 * **Sends only what `AccountUpdate` accepts** — `opening_balance` and
 * `institution_name`, and only the ones the caller actually supplied.
 * `name`/`type` are not in `AccountUpdate` at all; sending them would be
 * silently ignored server-side rather than rejected, which is worse than a
 * type error, so `ManualAccountEdit`'s shape rules them out at compile time
 * instead of at review time.
 */
export function useUpdateManualAccount(): {
  update: (id: string, edit: ManualAccountEdit) => Promise<Account>
  isPending: boolean
} {
  const client = useQueryClient()
  const mutation = useMutation({
    mutationFn: async ({ id, edit }: { id: string; edit: ManualAccountEdit }) => {
      const body: { opening_balance?: string; institution_name?: string | null } = {}
      if (edit.openingCents !== undefined) body.opening_balance = formatCents(edit.openingCents)
      if (edit.institutionName !== undefined) body.institution_name = edit.institutionName

      const { data, error, response } = await api.PATCH('/api/v1/accounts/{account_id}', {
        params: { path: { account_id: id } },
        body,
      })
      if (error !== undefined || data === undefined) throw new AccountsError(error, response.status)
      return toAccount(data)
    },
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.accounts.all() })
    },
  })

  return {
    update: (id, edit) => mutation.mutateAsync({ id, edit }),
    isPending: mutation.isPending,
  }
}
